import {
  AdvanceQualificationMethod,
  LendingPaymentSource,
} from '@varo/graphql-schema-types';
import { Stack, useLocalSearchParams, useRouter } from 'expo-router';
import {
  getFlowNameForAnalytics,
  TabaPayEntryFlowType,
} from 'features/moveMoney/screens/dcf/tabapay/helpers/TabaPay.helpers';
import { useTabaPayAddress } from 'features/moveMoney/screens/dcf/tabapay/providers/TabaPayContextProvider';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { StyleSheet, View } from 'react-native';
import { usePressableAnalytics } from 'shared/hooks/analytics/usePressableAnalytics';
import { useViewDidFocusScreenAnalyticsEvent } from 'shared/hooks/analytics/useViewDidFocusAnalytics';
import { useBackHandler } from 'shared/hooks/useBackHandler/useBackHandler';
import SuccessScreen from 'shared/patterns/vxl/SuccessScreen/SuccessScreen';
import theme from 'shared/styles/theme';

const TabapaySuccessScreen = () => {
  const router = useRouter();
  const { t: translatedText } = useTranslation('moveMoney', {
    keyPrefix: 'tabapayCardAdded',
  });

  const {
    tabaPayEntryFlowType = TabaPayEntryFlowType.ExternalAccount,
    dueDate,
    feeAmount,
    amountId,
    advanceAmount,
    repaymentDate,
    selectedAccountId,
    paymentSource,
    selectedAccountName,
    id,
    qualificationMethod,
    autoPaymentAgreementId,
  } = useLocalSearchParams<{
    tabaPayEntryFlowType?: TabaPayEntryFlowType;
    dueDate?: string;
    feeAmount?: string;
    amountId?: string;
    advanceAmount?: string;
    repaymentDate?: string;
    selectedAccountId?: string;
    paymentSource?: LendingPaymentSource;
    selectedAccountName?: string;
    id?: string; // advance account id for VA manual payment flow
    qualificationMethod: AdvanceQualificationMethod;
    autoPaymentAgreementId?: string; // string ex: "1,2,3"
  }>();

  const flowName = getFlowNameForAnalytics(tabaPayEntryFlowType);

  const analyticsCommonAttributes = {
    subcategory: flowName,
    flow_name: flowName,
  };

  useViewDidFocusScreenAnalyticsEvent({
    key: 'debit-card-added-success',
    customAttributes: analyticsCommonAttributes,
  });

  const nextButtonOnPressEvent = usePressableAnalytics({
    analyticsId: 'debitCardAddedSuccess',
    customAttributes: analyticsCommonAttributes,
  });

  const { setShouldRefreshCards } = useTabaPayAddress();

  const backToEntryPoint = () => {
    setShouldRefreshCards(true);

    switch (tabaPayEntryFlowType) {
      case TabaPayEntryFlowType.DCFFlow:
        router.back();
        break;
      case TabaPayEntryFlowType.VaApplication:
        router.back();
        router.dismissTo({
          pathname: '/varo-advance/review',
          params: {
            advanceAmount,
            dueDate,
            feeAmount,
            amountId,
            repaymentDate,
            selectedAccountId,
            paymentSource,
            selectedAccountName,
            qualificationMethod,
            autoPaymentAgreementId,
          },
        });
        break;
      case TabaPayEntryFlowType.AutoPay:
        router.dismissTo({
          pathname: '/varo-advance/autopayment',
          params: {
            selectedAmount: advanceAmount,
            feeAmount: feeAmount,
            optionId: amountId,
            fromDebitCardFlow: 'true',
            qualificationMethod,
          },
        });
        break;
      case TabaPayEntryFlowType.VaPayment:
        router.dismissTo({
          pathname: '/accounts/varo-advance/manual-repayment',
          params: {
            id,
            fromDebitCardFlow: 'true',
          },
        });
        break;
      case TabaPayEntryFlowType.ExternalAccount:
      default:
        router.dismissTo('/myvaro/external-accounts');
        break;
    }
  };

  useBackHandler(backToEntryPoint);

  return (
    <View style={styles.container}>
      <Stack.Screen
        options={{
          headerBackVisible: false,
          headerShown: false,
          gestureEnabled: false,
        }}
      />
      <SuccessScreen
        primaryButton={{
          label: translatedText('buttons.next'),
          onPress: () => {
            nextButtonOnPressEvent();
            backToEntryPoint();
          },
        }}
        eyebrowText={translatedText('eyeBrowText')}
        visual="CARD_B"
        titleText={translatedText('title')}
      />
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    backgroundColor: theme.color.background.primary,
    flex: 1,
  },
});

export default TabapaySuccessScreen;
